import React from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, CheckCircle, MapPin} from 'lucide-react'

export default function TherapyPage() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#4A90E2] to-[#48C9B0] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
              Therapy for Anxiety: Evidence-Based Treatment Options
            </h1>
            <p className="text-xl text-blue-100">
              Discover proven therapeutic approaches that help millions of Americans manage and overcome anxiety disorders.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-64 md:h-96 overflow-hidden">
        <img
          src="https://static.lumi.new/16/16453723c3cd8b3535292315de43ec4a.png"
          alt="Professional therapy session"
          className="w-full h-full object-cover"
        />
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* CBT Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Cognitive Behavioral Therapy for Anxiety: The Gold Standard Treatment
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              <strong>Cognitive Behavioral Therapy (CBT)</strong> is widely recognized as the most effective psychological treatment for anxiety disorders in the United States. Backed by decades of research, CBT has helped millions of Americans reduce anxiety symptoms and regain control of their lives.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              CBT for anxiety works by identifying and changing negative thought patterns and behaviors that contribute to anxious feelings. Unlike medication alone, CBT teaches you practical skills you can use for life.
            </p>

            <div className="bg-blue-50 border-l-4 border-[#4A90E2] p-6 mb-8 rounded-r-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Why CBT is Effective for Anxiety</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Evidence-Based:</strong> Supported by over 500 clinical trials</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Skill-Building:</strong> Teaches lasting coping mechanisms</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Time-Limited:</strong> Typically 12-16 sessions show significant improvement</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-[#48C9B0] mr-3 mt-1 flex-shrink-0" />
                  <span className="text-gray-700"><strong>Long-Term Results:</strong> Skills remain effective years after treatment</span>
                </li>
              </ul>
            </div>
          </div>

          {/* How CBT Works */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How CBT for Anxiety Works</h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-[#4A90E2] mb-3">1. Identify Thought Patterns</h3>
                <p className="text-gray-700">
                  Learn to recognize automatic negative thoughts and cognitive distortions that trigger anxiety responses.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-[#4A90E2] mb-3">2. Challenge Anxious Thoughts</h3>
                <p className="text-gray-700">
                  Examine evidence for and against anxious beliefs, developing more balanced and realistic perspectives.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-[#4A90E2] mb-3">3. Change Behavioral Responses</h3>
                <p className="text-gray-700">
                  Gradually face feared situations through exposure therapy while using coping skills to manage anxiety.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-[#4A90E2] mb-3">4. Build Lasting Skills</h3>
                <p className="text-gray-700">
                  Practice relaxation techniques, mindfulness, and problem-solving strategies you'll use long after therapy ends.
                </p>
              </div>
            </div>
          </div>

          {/* Other Treatment Options */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Other Effective Treatments for Anxiety</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Medication</h3>
                <p className="text-gray-700">
                  SSRIs, SNRIs, and benzodiazepines can help manage symptoms when prescribed by a psychiatrist.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Acceptance and Commitment Therapy (ACT)</h3>
                <p className="text-gray-700">
                  Learn to accept anxious thoughts while committing to value-based actions.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Exposure Therapy</h3>
                <p className="text-gray-700">
                  Systematic desensitization to feared situations reduces avoidance behaviors.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Mindfulness-Based Therapies</h3>
                <p className="text-gray-700">
                  MBSR and MBCT help reduce anxiety through present-moment awareness.
                </p>
              </div>
            </div>
          </div>

          {/* Find a Therapist Section */}
          <div className="bg-gradient-to-br from-[#4A90E2] to-[#5DADE2] rounded-2xl p-8 md:p-12 text-white">
            <div className="flex items-start mb-6">
              <MapPin className="w-8 h-8 mr-4 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold mb-4">How to Find a Therapist for Anxiety</h2>
                <p className="text-lg text-blue-100 mb-6">
                  Finding the right therapist is crucial for effective anxiety treatment. Here are trusted resources to connect with licensed mental health professionals in the USA:
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">Psychology Today</h3>
                <p className="text-blue-100 mb-4">Search by location, insurance, and specialty</p>
                <a
                  href="https://www.psychologytoday.com/us/therapists"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white font-semibold underline hover:text-blue-100"
                >
                  Visit Directory →
                </a>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">ADAA Therapist Directory</h3>
                <p className="text-blue-100 mb-4">Anxiety and Depression Association of America</p>
                <a
                  href="https://members.adaa.org/page/FATMain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white font-semibold underline hover:text-blue-100"
                >
                  Find Specialists →
                </a>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">SAMHSA National Helpline</h3>
                <p className="text-blue-100 mb-4">Free, confidential, 24/7 referral service</p>
                <a
                  href="tel:1-800-662-4357"
                  className="text-white font-semibold underline hover:text-blue-100"
                >
                  1-800-662-HELP (4357)
                </a>
              </div>
              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-bold mb-3">Your Insurance Provider</h3>
                <p className="text-blue-100 mb-4">Check your plan's mental health coverage and network</p>
                <p className="text-white font-semibold">Contact your insurer directly</p>
              </div>
            </div>

            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3">What to Look For in a Therapist</h3>
              <ul className="space-y-2 text-blue-100">
                <li>✓ Licensed professional (LCSW, PhD, PsyD, or LMFT)</li>
                <li>✓ Specialization in anxiety disorders</li>
                <li>✓ Training in CBT or evidence-based therapies</li>
                <li>✓ Accepts your insurance or offers sliding scale fees</li>
                <li>✓ Good rapport and communication style that fits you</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Not Sure Where to Start?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Take our free GAD-7 assessment to understand your anxiety levels
          </p>
          <Link
            to="/assessment"
            className="inline-flex items-center justify-center px-8 py-4 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all shadow-lg"
          >
            Take the GAD-7 Quiz
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  )
}
